<template>
  <div class="twofa">
    <div class="twofa__status">
      <span class="material-symbols-outlined twofa__status-icon" :class="enabled ? 'twofa__status-icon--on' : 'twofa__status-icon--off'">
        {{ enabled ? 'verified_user' : 'shield' }}
      </span>
      <div>
        <p class="twofa__status-label">Two-factor authentication</p>
        <p class="twofa__status-value" :class="enabled ? 'twofa__on' : 'twofa__off'">
          {{ enabled ? 'Enabled' : 'Disabled' }}
        </p>
      </div>
    </div>

    <!-- Enable flow -->
    <template v-if="!enabled">
      <template v-if="setupStep === 'idle'">
        <p class="twofa__description">
          Add an extra layer of security to your account. Once enabled, you will need a code from
          your authenticator app (Google Authenticator, Authy…) at each login.
        </p>
        <n-button type="primary" @click="startSetup" :loading="loading">
          Set up authenticator
        </n-button>
      </template>

      <template v-else-if="setupStep === 'scan'">
        <p class="twofa__instruction">
          Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.
        </p>
        <div class="twofa__qr-wrap">
          <img v-if="qrDataUrl" :src="qrDataUrl" alt="TOTP QR code" class="twofa__qr" />
          <div v-else class="twofa__qr-placeholder">Generating…</div>
        </div>
        <details class="twofa__secret-details">
          <summary>Can't scan? Enter code manually</summary>
          <code class="twofa__secret">{{ pendingSecret }}</code>
        </details>
        <n-form-item label="Authenticator code" style="margin-top: 1.25rem">
          <n-input
            v-model:value="verifyCode"
            placeholder="000000"
            size="large"
            maxlength="6"
            :allow-input="onlyDigits"
            @keyup.enter="confirmEnable"
          />
        </n-form-item>
        <div class="twofa__actions">
          <n-button @click="setupStep = 'idle'">Cancel</n-button>
          <n-button type="primary" :loading="loading" @click="confirmEnable">
            Confirm & enable
          </n-button>
        </div>
      </template>
    </template>

    <!-- Disable flow -->
    <template v-else>
      <template v-if="disableStep === 'idle'">
        <p class="twofa__description">
          Your account is protected by an authenticator app. To disable 2FA, enter your current
          authenticator code.
        </p>
        <n-button type="error" @click="disableStep = 'confirm'">
          Disable 2FA
        </n-button>
      </template>

      <template v-else>
        <n-form-item label="Authenticator code">
          <n-input
            v-model:value="disableCode"
            placeholder="000000"
            size="large"
            maxlength="6"
            :allow-input="onlyDigits"
            @keyup.enter="confirmDisable"
          />
        </n-form-item>
        <div class="twofa__actions">
          <n-button @click="disableStep = 'idle'">Cancel</n-button>
          <n-button type="error" :loading="loading" @click="confirmDisable">
            Disable
          </n-button>
        </div>
      </template>
    </template>

    <n-alert v-if="error" type="error" style="margin-top: 1rem" closable @close="error = null">
      {{ error }}
    </n-alert>
    <n-alert v-if="success" type="success" style="margin-top: 1rem" closable @close="success = null">
      {{ success }}
    </n-alert>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import QRCode from 'qrcode';
import { NButton, NFormItem, NInput, NAlert } from 'naive-ui';
import { useAuthStore } from '@/stores/authStore';
import { API_URL, authFetch } from '@/api';

const authStore = useAuthStore();

const enabled = computed(() => authStore.user?.totp_enabled ?? false);

const loading = ref(false);
const error = ref(null);
const success = ref(null);

const setupStep = ref('idle'); // 'idle' | 'scan'
const pendingSecret = ref('');
const qrDataUrl = ref('');
const verifyCode = ref('');

const disableStep = ref('idle'); // 'idle' | 'confirm'
const disableCode = ref('');

const onlyDigits = (val) => /^\d*$/.test(val);

async function startSetup() {
  error.value = null;
  loading.value = true;
  try {
    const res = await authFetch(`${API_URL}/auth/totp/setup`);
    if (!res.ok) throw new Error('Failed to generate setup data');
    const data = await res.json();
    pendingSecret.value = data.secret;
    qrDataUrl.value = await QRCode.toDataURL(data.uri, { width: 200, margin: 1 });
    setupStep.value = 'scan';
  } catch (e) {
    error.value = e.message;
  } finally {
    loading.value = false;
  }
}

async function confirmEnable() {
  error.value = null;
  if (verifyCode.value.length !== 6) {
    error.value = 'Please enter the 6-digit code';
    return;
  }
  loading.value = true;
  try {
    const res = await authFetch(`${API_URL}/auth/totp/enable`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ secret: pendingSecret.value, code: verifyCode.value }),
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.detail || 'Failed to enable 2FA');
    }
    await authStore.fetchUser();
    setupStep.value = 'idle';
    verifyCode.value = '';
    pendingSecret.value = '';
    qrDataUrl.value = '';
    success.value = '2FA has been enabled on your account.';
  } catch (e) {
    error.value = e.message;
    verifyCode.value = '';
  } finally {
    loading.value = false;
  }
}

async function confirmDisable() {
  error.value = null;
  if (disableCode.value.length !== 6) {
    error.value = 'Please enter the 6-digit code';
    return;
  }
  loading.value = true;
  try {
    const res = await authFetch(`${API_URL}/auth/totp/disable`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: disableCode.value }),
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.detail || 'Failed to disable 2FA');
    }
    await authStore.fetchUser();
    disableStep.value = 'idle';
    disableCode.value = '';
    success.value = '2FA has been disabled.';
  } catch (e) {
    error.value = e.message;
    disableCode.value = '';
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.twofa {
  max-width: 480px;
}

.twofa__status {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  background: var(--al-surface-container);
  border-radius: var(--al-radius);
}

.twofa__status-icon {
  font-size: 2rem;
}
.twofa__status-icon--on { color: #4caf50; }
.twofa__status-icon--off { color: var(--al-on-surface-variant); }

.twofa__status-label {
  margin: 0;
  font-size: 0.875rem;
  color: var(--al-on-surface-variant);
}

.twofa__status-value {
  margin: 0.2rem 0 0;
  font-weight: 700;
  font-size: 1rem;
}
.twofa__on { color: #4caf50; }
.twofa__off { color: var(--al-on-surface-variant); }

.twofa__description {
  font-size: 0.9rem;
  color: var(--al-on-surface-variant);
  margin: 0 0 1.25rem;
  line-height: 1.6;
}

.twofa__instruction {
  font-size: 0.9rem;
  color: var(--al-on-surface-variant);
  margin: 0 0 1rem;
  line-height: 1.6;
}

.twofa__qr-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.twofa__qr {
  border-radius: var(--al-radius-sm);
  border: 4px solid white;
}

.twofa__qr-placeholder {
  width: 200px;
  height: 200px;
  background: var(--al-surface-container);
  border-radius: var(--al-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--al-on-surface-variant);
  font-size: 0.875rem;
}

.twofa__secret-details {
  font-size: 0.825rem;
  color: var(--al-on-surface-variant);
  margin-bottom: 0.5rem;
}
.twofa__secret-details summary {
  cursor: pointer;
}

.twofa__secret {
  display: block;
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--al-surface-container);
  border-radius: var(--al-radius-sm);
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  word-break: break-all;
  color: var(--al-primary);
}

.twofa__actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
}
</style>
